import CheckIcon from "@mui/icons-material/Check";
import MailOutlineIcon from "@mui/icons-material/EmailOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Collapse from "@mui/material/Collapse";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, type FC, type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useTRPC } from "src/api/trpc";
import { currentReturnTo } from "src/auth/returnTo";
import { companyHint, isPersonalEmail, looksLikeEmail } from "src/pages/SignInPage/workEmail";

const RESEND_COOLDOWN_SECONDS = 30;
const SECOND_MS = 1000;
const ICON_CIRCLE = 96;

interface SentState {
	email: string;
	expiresInMinutes: number;
	localLink?: string;
}

interface RegistrationCardProps {
	/** "Already have an account? Sign in" — DXM SSO sign-in (and dev login locally). */
	signIn?: ReactNode;
}

/** Counts down to zero once a second; restarts whenever `from` changes identity. */
const useCountdown = (from: number, key: unknown) => {
	const [left, setLeft] = useState(from);
	useEffect(() => {
		setLeft(from);
		if (!key) return;
		const timer = window.setInterval(() => setLeft(value => Math.max(0, value - 1)), SECOND_MS);
		return () => window.clearInterval(timer);
	}, [from, key]);
	return left;
};

/**
 * Figma D02 "Create your free account" → D03 "Check your inbox" (wireframe 2:635): only the work
 * email is required; name and phone are optional behind a toggle. The API emails a single-use
 * sign-in link, which both registers and signs in.
 */
export const RegistrationCard: FC<RegistrationCardProps> = ({ signIn }) => {
	const { t } = useTranslation();
	const trpc = useTRPC();
	const request = useMutation(trpc.v1.session.requestSignInLink.mutationOptions());
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [phone, setPhone] = useState("");
	const [showOptional, setShowOptional] = useState(false);
	const [touched, setTouched] = useState(false);
	const [sent, setSent] = useState<SentState>();
	const [sentAt, setSentAt] = useState<number>();
	const resendIn = useCountdown(RESEND_COOLDOWN_SECONDS, sentAt);

	const trimmed = email.trim();
	const personal = looksLikeEmail(trimmed) && isPersonalEmail(trimmed);
	const emailError =
		touched && trimmed && !looksLikeEmail(trimmed)
			? t("register.invalidEmail")
			: personal
				? t("register.workEmailOnly")
				: undefined;
	const company = looksLikeEmail(trimmed) && !personal ? companyHint(trimmed) : undefined;

	const handleEmail = useCallback((event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value), []);
	const handleBlur = useCallback(() => setTouched(true), []);
	const handleName = useCallback((event: ChangeEvent<HTMLInputElement>) => setName(event.target.value), []);
	const handlePhone = useCallback((event: ChangeEvent<HTMLInputElement>) => setPhone(event.target.value), []);
	const toggleOptional = useCallback(() => setShowOptional(value => !value), []);

	const send = useCallback(
		async (target: string) => {
			const result = await request.mutateAsync({
				email: target,
				name: name.trim() || undefined,
				phone: phone.trim() || undefined,
				// The emailed link may open in another tab, so it carries the page itself.
				returnTo: currentReturnTo()
			});
			setSent({ email: result.email, expiresInMinutes: result.expiresInMinutes, localLink: result.localLink });
			setSentAt(Date.now());
		},
		[name, phone, request]
	);
	const handleSubmit = useCallback(
		(event: FormEvent) => {
			event.preventDefault();
			setTouched(true);
			if (!looksLikeEmail(trimmed) || personal) return;
			void send(trimmed).catch(() => undefined);
		},
		[personal, send, trimmed]
	);
	const resend = useCallback(() => {
		if (sent) void send(sent.email).catch(() => undefined);
	}, [send, sent]);
	const useDifferentEmail = useCallback(() => {
		request.reset();
		setSent(undefined);
		setSentAt(undefined);
	}, [request]);

	if (sent) {
		return (
			<Card component="section" aria-live="polite" sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
				<Box
					aria-hidden
					sx={{
						width: ICON_CIRCLE,
						height: ICON_CIRCLE,
						mx: "auto",
						borderRadius: "50%",
						display: "grid",
						placeItems: "center",
						bgcolor: "brand.avatarTint",
						color: "primary.main"
					}}>
					<MailOutlineIcon sx={{ fontSize: 40 }} />
				</Box>
				<Typography variant="h1" component="h2" sx={{ mt: 3 }}>
					{t("register.sent.title")}
				</Typography>
				<Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
					{t("register.sent.body", { email: sent.email, minutes: sent.expiresInMinutes })}
				</Typography>
				<Box
					sx={{ mt: 3, p: 2, borderRadius: "16px", border: 1, borderColor: "divider", bgcolor: "background.default" }}>
					<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", textAlign: "left" }}>
						<Box
							sx={{
								width: 40,
								height: 40,
								flexShrink: 0,
								display: "grid",
								placeItems: "center",
								borderRadius: "10px",
								bgcolor: "background.paper",
								color: "brand.positiveText"
							}}>
							<CheckIcon fontSize="small" />
						</Box>
						<Box>
							<Typography variant="subtitle1">{t("register.sent.nextTitle")}</Typography>
							<Typography variant="body2" color="text.secondary">
								{t("register.sent.nextBody")}
							</Typography>
						</Box>
					</Stack>
				</Box>
				{request.error && (
					<Alert severity="error" sx={{ mt: 2, textAlign: "left" }}>
						{request.error.message}
					</Alert>
				)}
				<Stack spacing={1.5} sx={{ mt: 4 }}>
					{sent.localLink && (
						<Button variant="contained" href={sent.localLink} startIcon={<MailOutlineIcon />}>
							{t("register.sent.openLocal")}
						</Button>
					)}
					<Button variant="outlined" onClick={resend} disabled={resendIn > 0 || request.isPending}>
						{resendIn > 0 ? t("register.sent.resendIn", { seconds: resendIn }) : t("register.sent.resend")}
					</Button>
					<Link component="button" type="button" variant="subtitle2" onClick={useDifferentEmail}>
						{t("register.sent.differentEmail")}
					</Link>
				</Stack>
			</Card>
		);
	}

	return (
		<Card component="section" aria-labelledby="register-title" sx={{ p: { xs: 3, sm: 4 } }}>
			<Typography id="register-title" variant="h1" component="h2">
				{t("register.title")}
			</Typography>
			<Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
				{t("register.subtitle")}
			</Typography>
			<Stack component="form" noValidate onSubmit={handleSubmit} spacing={2.5} sx={{ mt: 3 }}>
				<Box>
					<Typography
						component="label"
						htmlFor="register-email"
						variant="subtitle2"
						sx={{ display: "block", mb: 0.75 }}>
						{t("register.workEmail")}
					</Typography>
					<TextField
						id="register-email"
						fullWidth
						type="email"
						autoComplete="email"
						autoFocus
						value={email}
						onChange={handleEmail}
						onBlur={handleBlur}
						placeholder={t("register.emailPlaceholder")}
						error={Boolean(emailError)}
						helperText={emailError ?? (company ? t("register.companyHint", { company }) : " ")}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<MailOutlineIcon fontSize="small" color={trimmed ? "primary" : undefined} />
									</InputAdornment>
								)
							}
						}}
					/>
				</Box>
				<Box sx={{ mt: "0 !important" }}>
					<Button
						onClick={toggleOptional}
						size="small"
						aria-expanded={showOptional}
						startIcon={showOptional ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						sx={{ px: 0.5, color: "primary.main" }}>
						{showOptional ? t("register.hideOptional") : t("register.showOptional")}
					</Button>
					<Collapse in={showOptional}>
						<Stack spacing={2.5} sx={{ pt: 1.5 }}>
							<Box>
								<Typography
									component="label"
									htmlFor="register-name"
									variant="subtitle2"
									sx={{ display: "block", mb: 0.75 }}>
									{t("register.name")}{" "}
									<Typography component="span" variant="body2" color="text.secondary">
										{t("register.optional")}
									</Typography>
								</Typography>
								<TextField id="register-name" fullWidth autoComplete="name" value={name} onChange={handleName} />
							</Box>
							<Box>
								<Typography
									component="label"
									htmlFor="register-phone"
									variant="subtitle2"
									sx={{ display: "block", mb: 0.75 }}>
									{t("register.phone")}{" "}
									<Typography component="span" variant="body2" color="text.secondary">
										{t("register.optional")}
									</Typography>
								</Typography>
								<TextField
									id="register-phone"
									fullWidth
									type="tel"
									autoComplete="tel"
									value={phone}
									onChange={handlePhone}
									placeholder={t("register.phonePlaceholder")}
								/>
							</Box>
						</Stack>
					</Collapse>
				</Box>
				{request.error && <Alert severity="error">{request.error.message}</Alert>}
				<Button
					type="submit"
					variant="contained"
					fullWidth
					startIcon={<MailOutlineIcon />}
					disabled={request.isPending}
					sx={{ fontSize: 16 }}>
					{t("register.submit")}
				</Button>
				<Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
					{t("register.terms")}
				</Typography>
				{signIn && (
					<Typography variant="body2" sx={{ textAlign: "center" }}>
						{t("register.haveAccount")} {signIn}
					</Typography>
				)}
			</Stack>
		</Card>
	);
};
